/**
 * @file AdminTwoFactorManagement.jsx
 * @description Admin interface for managing user two-factor authentication
 *
 * @section Responsibilities
 * - View user 2FA status
 * - Reset user 2FA
 * - Manage 2FA settings
 * - Handle 2FA support requests
 *
 * @section Dependencies
 * - Request: API communication
 * - User management
 * - Admin permissions
 *
 * @section Integration Points
 * - User management API
 * - 2FA admin endpoints
 * - Admin authentication
 *
 * @section State Management
 * - users: List of users
 * - selectedUser: Currently selected user
 * - loading: Process status
 * - filter: User filtering
 *
 * @section Events
 * - onResetTwoFactor: Reset user 2FA
 * - onViewStatus: View user 2FA status
 * - onSearch: Search users
 *
 * @section Props
 * @prop {Array} users - List of users
 * @prop {Function} onUserUpdate - User update callback
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import DgButton from "../../../../components/DgButton";
import Request from "../../../../utils/fetchUtils";
import Swal from "sweetalert2";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import SearchInput from "../../../../components/forms/SearchInput";

const AdminTwoFactorManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await Request("/user", {}, "GET");
      if (response.ok) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = useCallback(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);  const fetchUserDetails = async (userKey) => {
    setLoading(true);
    try {

      const response = await Request(`/user/2fa/status?userKey=${encodeURIComponent(userKey)}`, {}, "GET");
      

      
      if (response.ok && response.data) {
        setUserDetails(response.data);
        // Guardar el usuario seleccionado para usarlo al resetear 2FA
        const selectedUserData = users.find(user => user.SK === userKey);
        setSelectedUser(selectedUserData);
        setDetailsOpen(true);
      } else {
        console.error("Failed to fetch user details:", response);
        Swal.fire("Error", response.message || "Failed to fetch user 2FA details", "error");
      }
    } catch (error) {
      console.error("Exception fetching user details:", error);
      Swal.fire("Error", "Failed to fetch user 2FA details: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };const resetUserTwoFactor = async (userKey, userName) => {
    const confirm = await Swal.fire({
      title: "Reset Two-Factor Authentication",
      text: `Are you sure you want to reset 2FA for ${userName}? This will disable their 2FA and they will need to set it up again.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset 2FA",
      confirmButtonColor: "#d33",
    });

    if (confirm.isConfirmed) {
      setLoading(true);      
      try {

        const response = await Request(
          `/user/2fa/admin-reset?targetUserKey=${encodeURIComponent(userKey)}`,
          {},
          "DELETE"
        );


        // Verificar múltiples condiciones de éxito
        if (response.ok || 
            (response.data && response.data.success) || 
            (response.status >= 200 && response.status < 300)) {
          Swal.fire("Success", "Two-factor authentication has been reset successfully", "success");
          await fetchUsers(); // Refresh user list
          if (detailsOpen) {
            setDetailsOpen(false);
          }
        } else {
          console.error("Reset 2FA failed:", response);
          
          // Mensaje de error más detallado
          let errorMessage = "Failed to reset 2FA";
          if (response.data && response.data.error) {
            errorMessage = response.data.error;
          } else if (response.message) {
            errorMessage = response.message;
          } else if (response.error) {
            errorMessage = response.error;
          }
          
          console.error("Final error message:", errorMessage);
          Swal.fire("Error", errorMessage, "error");
        }
      } catch (error) {
        console.error("Reset 2FA exception:", error);
        Swal.fire("Error", "Failed to reset 2FA: " + error.message, "error");
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <SkinPage>
      <Widget name={"2fa-management"} width={"full-width"}>
        <div className="toppers">
          <div className="new-client" onClick={fetchUsers}>
            <img className="icon" src="/assets/icons/refresh.svg" alt="" />
            <p className="new-client-button">Refresh Users</p>
          </div>
          <div className="filters">
            <div>
              <div className="filter-title form-field">
                <SearchInput
                  customInput={{
                    label: "Search by name or email",
                    name: "search",
                    value: searchTerm,
                    action: (e) => setSearchTerm(e.target.value),
                    tooltipBool: true,
                    tooltipText: "Search for a user by name or email",
                    placeholder: "Type a name or email...",
                  }}
                />
              </div>
            </div>
            <div>
              <button 
                onClick={() => {
                  setSearchTerm("");
                  setFilteredUsers(users);
                }}
                className="reset-filters-btn"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        <table className="users-management-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>2FA Status</th>
              <th>Backup Codes</th>
              <th>Last Verification</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <tr key={index}>
                  <td className="name">
                    <div className="user">
                      <div>
                        <span className="name">{user.fullName || "N/A"}</span>
                      </div>
                    </div>
                  </td>
                  <td className="email">
                    <div className="email">
                      <div>
                        <span className="name">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="strategy">
                    <span className="strategy-title">{user.role}</span>
                  </td>
                  <td className="account">
                    {user.twoFactorEnabled ? (
                      <span style={{ color: "#75a180" }}>Enabled</span>
                    ) : (
                      <span style={{ color: "#ff5e5b" }}>Disabled</span>
                    )}
                  </td>
                  <td className="account">
                    <span>{user.twoFactorBackupCodes?.length || 0} codes</span>
                  </td>
                  <td className="account">
                    <span>
                      {user.lastTwoFactorVerification
                        ? new Date(user.lastTwoFactorVerification).toLocaleDateString()
                        : "Never"}
                    </span>
                  </td>
                  <td className="fees">
                    <div className="fees-detail">
                      <IconButton
                        size="small"
                        onClick={() => fetchUserDetails(user.SK)}
                        title="View Details"
                      >
                        <SecurityIcon fontSize="small" style={{ color: "white" }} />
                      </IconButton>
                      {user.twoFactorEnabled && (
                        <IconButton
                          size="small"
                          onClick={() => resetUserTwoFactor(user.SK, user.fullName)}
                          title="Reset 2FA"
                          style={{ color: "#ff5e5b" }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  {searchTerm ? "No users found matching your search." : "No users found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Widget>

      {/* User Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Two-Factor Authentication Details</DialogTitle>        <DialogContent>
          {userDetails && (
            <Box>
              <Alert
                severity={userDetails.enabled ? "success" : "warning"}
                sx={{ mb: 2 }}
              >
                2FA is {userDetails.enabled ? "enabled" : "disabled"} for this user
              </Alert>

              {userDetails.userEmail && (
                <Box mb={2} sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    User: <strong>{selectedUser?.fullName || "Unknown"}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {userDetails.userEmail}
                  </Typography>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Status Information:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • 2FA Enabled: {userDetails.enabled ? "Yes" : "No"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • Backup Codes: {userDetails.backupCodesCount} available
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • Last Verification:{" "}
                  {userDetails.lastVerification
                    ? new Date(userDetails.lastVerification).toLocaleString()
                    : "Never"}
                </Typography>
              </Box>

              {userDetails.enabled && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  User has an active 2FA setup. Resetting will require them to configure 2FA again.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <DgButton
            category="secondary"
            text="Close"
            action={() => setDetailsOpen(false)}
          />
          {userDetails?.enabled && (
            <DgButton
              category="danger"
              text="Reset 2FA"
              action={() => {
                setDetailsOpen(false);
                resetUserTwoFactor(selectedUser?.SK, selectedUser?.fullName);
              }}
            />
          )}        </DialogActions>
      </Dialog>
    </SkinPage>
  );
};

export default AdminTwoFactorManagement;
